import { Userpilot } from 'userpilot'
import { USER_INFO_KEY } from "../constants/common";
import { get } from "lodash";
import { isImpersonatedToken } from "../helpers/impersonationHelper";


// Global Privacy Control: Userpilot must not load, identify or track a browser
// that sends the signal. Read live so every entry point honours it.
const privacySignal = () =>
  typeof navigator !== "undefined" && navigator.globalPrivacyControl === true;

export const initUserPilot = (token) => {
  if (privacySignal()) return;
  Userpilot.initialize(token);
};

export const identifyUser = (userId, userData) => {
  if (privacySignal()) return;
  Userpilot.identify(userId, userData);
};

// Mirrors services/analytics.js's reset() (PostHog) for the same reason:
// without this, whatever identity Userpilot already holds from a prior
// (typically the admin's own) session in this browser keeps being used
// for flow/survey targeting through an impersonated session - merely
// skipping identify() doesn't clear it.
export const resetUserPilot = () => {
  if (privacySignal()) return;
  Userpilot.reset();
};

export const identifyUserByLocalStorage = (userDetailsFromStore) => {
  return new Promise((resolve) => {
    const user = JSON.parse(localStorage.getItem(USER_INFO_KEY) || '{}');
    const userInfo = get(user, 'userInfo');

    // Must never send the impersonated customer's name/email to Userpilot
    // under the admin's browsing session - see the equivalent guard in
    // useAnalyticsIdentify.js's resolveIdentity. Actively resets rather
    // than just skipping identify(), so a residual prior identity in this
    // browser doesn't keep being used for flow targeting.
    if(!userInfo || isImpersonatedToken(get(user, 'token.accessToken'))) {
      resetUserPilot();
      resolve();
      return;
    }

    identifyUser(
      userInfo.userId,
      {
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        name: [userInfo.firstName, userInfo.lastName].filter(Boolean).join(' '),
        email: user.email || '',
        userType: user.userType || '',
        planName: get(userDetailsFromStore, '0.PlanName'),
        planPeriod: get(userDetailsFromStore, '0.period')
      }
    );
    resolve();
  });
}


export const trackEvent = (eventName, properties) => {
  if (privacySignal()) return;
  Userpilot.track(eventName, properties);
};

export const trigger = (token) => {
  if (privacySignal()) return;
  Userpilot.trigger(token)
}

export const triggerOnboarding = async () => {
  await identifyUserByLocalStorage();
  trigger(process.env.REACT_APP_USERPILOT_ONBOARDING_FLOW_KEY);
}


export const showUserPilotOnboarding = () => {
  localStorage.setItem("showUserPilotOnboarding","true")
}

export const reloadUserPilot = () => {
  if (privacySignal()) return;
  Userpilot.reload()
}

export default Userpilot;
